import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ItemsFromAI, ItemsFromAIFilePayload } from 'src/app/shared/models/data/itemsFromIA.model';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';

@Component({
  selector: 'app-modal-upload-items-ia',
  templateUrl: './modal-upload-items-ia.component.html',
  styleUrls: ['./modal-upload-items-ia.component.scss']
})
export class ModalUploadItemsIAComponent implements OnDestroy {
  @Input() isMobile: boolean = false;
  @Input() theme: string = "silver";
  @Input() enquiryNo!: string;
  @Input() readOnly: boolean = false;

  @Output() onCancel = new EventEmitter<void>();
  @Output() onUploadComplete = new EventEmitter<ItemsFromAI>();

  constructor(private teiasoftToaster: TeiasoftToasterService, private translate: TranslateService, private backendService: BackendService, private teiasoftLoader: TeiasoftLoaderService) { }

  private countdownIntervalId: number | null = null;
  estimatedTimeLeft: number = 0;
  estimatedTimeStr: string = '';
  isDragging = false;
  isUploading = false;
  itemsCount: number = 0;
  processedItems: number = 0;
  progressPercentage: number = 0;
  processTakingLonger: boolean = false;
  selectedFileName: string = '';
  private selectedFileContent: string = '';


  onCancelClick() {
    this.stopCountdownTimer();
    this.onCancel.emit();
  }

  onSaveClick() {
    if (!this.selectedFileContent || !this.selectedFileName) {
      this.teiasoftToaster.show("info", this.translate.instant("enquiryBook.uploadItemWithIAFileRequired"));
      return;
    }

    this.uploadItemsFromFile({
      fileContent: this.selectedFileContent,
      fileName: this.selectedFileName
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }

    this.loadSelectedFile(files[0]);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files?.length) {
      return;
    }

    this.loadSelectedFile(files[0]);
    input.value = '';
  }

  private loadSelectedFile(file: File): void {
    this.selectedFileName = '';
    this.selectedFileContent = '';

    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      const fileContent = fileReader.result?.toString().replace('data:', '').replace(/^.+,/, '');
      if (!fileContent) {
        this.teiasoftToaster.show("info", this.translate.instant("enquiryBook.uploadItemWithIAFileReadError"));
        return;
      }

      this.selectedFileName = file.name;
      this.selectedFileContent = fileContent;
    };

    fileReader.onerror = () => {
      this.teiasoftToaster.show("info", this.translate.instant("enquiryBook.uploadItemWithIAFileReadError"));
    };
  }

  uploadItemsFromFile(payload: ItemsFromAIFilePayload) {
    this.teiasoftLoader.start();
    this.isUploading = true;
    this.backendService.countItemsToBeParsedFromFile(this.enquiryNo!, payload).subscribe((res) => {

      this.itemsCount = res.itemCount!;
      const totalSeconds = this.itemsCount * 2; // 2 seconds per item
      this.estimatedTimeLeft = totalSeconds;
      this.estimatedTimeStr = this.formatTime(totalSeconds);

      this.teiasoftLoader.stop();
      this.processTakingLonger = false;
      this.processedItems = 0;
      this.progressPercentage = 0;

      this.startCountdownTimer();


      this.backendService.parseItemsFromFile(this.enquiryNo!, payload).subscribe((res) => {
        this.onUploadComplete.emit(res);
        this.stopCountdownTimer();
        this.isUploading = false;
      }, (_error) => {
        this.stopCountdownTimer();
        this.isUploading = false;
        this.onCancel.emit();
      });
    }
      , (_error) => {
        this.teiasoftLoader.stop();
        this.isUploading = false;
        this.onCancel.emit();
      });
  }

  formatTime(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} sec`;
  }

  startCountdownTimer() {
    this.stopCountdownTimer();
    let processTick = 0; // Track processing intervals

    this.countdownIntervalId = window.setInterval(() => {
      if (this.estimatedTimeLeft > 0) {
        this.estimatedTimeLeft--;
        this.estimatedTimeStr = this.formatTime(this.estimatedTimeLeft);

        processTick++;

        // Process 1 item every 2 seconds
        if (processTick === 2) {
          processTick = 0; // Reset processTick every 2 seconds

          if (this.processedItems < this.itemsCount) {
            this.processedItems++;

            // Ensure we don't exceed itemsCount
            if (this.processedItems > this.itemsCount) {
              this.processedItems = this.itemsCount;
            }

            // Update progress percentage
            this.progressPercentage = (this.processedItems / this.itemsCount) * 100;
          }
        }

      } else {
        this.stopCountdownTimer();
        this.processTakingLonger = true;
      }
    }, 1000);
  }

  private stopCountdownTimer(): void {
    if (this.countdownIntervalId !== null) {
      window.clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopCountdownTimer();
  }

}
